import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, ChevronDown, ChevronRight, Send, StickyNote } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export type PatientStatus = "active" | "churned" | "pending" | "inactive";

export interface PatientRecord {
  email: string;
  name: string;
  status: PatientStatus;
  totalSpent: number;
  orderCount: number;
  lastOrderDate: string;
  orders: {
    id: string;
    status: string;
    amount_cents: number;
    order_type: string;
    created_at: string;
  }[];
}

const statusBadge: Record<PatientStatus, { label: string; variant: "default" | "secondary" | "outline" | "destructive" }> = {
  active: { label: "Active", variant: "default" },
  pending: { label: "Pending", variant: "secondary" },
  inactive: { label: "Inactive", variant: "outline" },
  churned: { label: "Churned", variant: "destructive" },
};

interface PatientPipelineProps {
  patients: PatientRecord[];
  formatAmount: (cents: number) => string;
}

const PatientPipeline = ({ patients, formatAmount }: PatientPipelineProps) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [noteText, setNoteText] = useState<Record<string, string>>({});
  const [noteOrderId, setNoteOrderId] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const toggle = (email: string) => {
    const next = new Set(expanded);
    next.has(email) ? next.delete(email) : next.add(email);
    setExpanded(next);
  };

  const filtered = patients.filter((p) => {
    const term = search.toLowerCase();
    const matchesSearch = p.name.toLowerCase().includes(term) || p.email.toLowerCase().includes(term);
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const addNote = async (patient: PatientRecord) => {
    const text = noteText[patient.email]?.trim();
    const orderId = noteOrderId[patient.email] || patient.orders[0]?.id;
    if (!text || !orderId || !user) return;

    setSubmitting(patient.email);
    const { error } = await supabase.from("order_notes").insert({
      order_id: orderId,
      admin_id: user.id,
      note: text,
    });
    setSubmitting(null);

    if (error) {
      toast({ title: "Failed to add note", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Note added" });
      setNoteText((prev) => ({ ...prev, [patient.email]: "" }));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-border">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search patients..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="churned">Churned</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-16 text-sm">No patients found</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Orders</TableHead>
                    <TableHead>Total Spent</TableHead>
                    <TableHead>Last Order</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((patient) => {
                    const isOpen = expanded.has(patient.email);
                    const badge = statusBadge[patient.status];
                    return (
                      <>
                        <TableRow
                          key={patient.email}
                          className="cursor-pointer hover:bg-secondary/30"
                          onClick={() => toggle(patient.email)}
                        >
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium">{patient.name || "—"}</p>
                          </TableCell>
                          <TableCell>
                            <Badge variant={badge.variant} className="text-xs">{badge.label}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">{patient.orderCount}</TableCell>
                          <TableCell className="font-medium">{formatAmount(patient.totalSpent)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {format(new Date(patient.lastOrderDate), "MMM d, yyyy")}
                          </TableCell>
                        </TableRow>
                        {isOpen && (
                          <TableRow key={`${patient.email}-detail`}>
                            <TableCell colSpan={6} className="bg-secondary/20 p-0">
                              <div className="p-4 space-y-4">
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-2">Order History</p>
                                  <div className="space-y-1.5">
                                    {patient.orders.map((o) => (
                                      <div key={o.id} className="flex items-center justify-between text-xs p-2 rounded bg-background border border-border">
                                        <span className="font-mono text-muted-foreground">{o.id.slice(0, 8)}…</span>
                                        <Badge variant="outline" className="text-[10px] capitalize">{o.order_type}</Badge>
                                        <Badge variant="secondary" className="text-[10px] capitalize">{o.status}</Badge>
                                        <span className="font-medium">{formatAmount(o.amount_cents)}</span>
                                        <span className="text-muted-foreground">{format(new Date(o.created_at), "MMM d, yyyy")}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Inline note form */}
                                <div className="border-t border-border pt-3">
                                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1">
                                    <StickyNote size={12} /> Add Note
                                  </p>
                                  {patient.orders.length > 1 && (
                                    <Select
                                      value={noteOrderId[patient.email] || patient.orders[0]?.id}
                                      onValueChange={(v) => setNoteOrderId((prev) => ({ ...prev, [patient.email]: v }))}
                                    >
                                      <SelectTrigger className="h-8 text-xs mb-2">
                                        <SelectValue placeholder="Select order" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {patient.orders.map((o) => (
                                          <SelectItem key={o.id} value={o.id}>
                                            {o.id.slice(0, 8)}… — {o.order_type} ({o.status})
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  )}
                                  <div className="flex gap-2">
                                    <Textarea
                                      placeholder="Write a note about this patient..."
                                      className="text-xs min-h-[60px] flex-1"
                                      value={noteText[patient.email] || ""}
                                      onChange={(e) => setNoteText((prev) => ({ ...prev, [patient.email]: e.target.value }))}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    <Button
                                      size="sm"
                                      className="h-auto self-end gap-1"
                                      disabled={!noteText[patient.email]?.trim() || submitting === patient.email}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addNote(patient);
                                      }}
                                    >
                                      <Send size={12} /> {submitting === patient.email ? "Saving…" : "Save"}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PatientPipeline;
